import { getUserMailboxInfo, MailboxInfo, MailboxType } from 'owa-client-ids';
import { getDisplayDateFromEwsDate, getEwsRequestString, parse } from 'owa-datetime';
import { cleanTimeZoneId } from 'owa-datetime-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getConfig } from 'owa-service/lib/config';
import BusyType from 'owa-service/lib/contract/BusyType';
import CalendarItem from 'owa-service/lib/contract/CalendarItem';
import CalendarItemExperienceType from 'owa-service/lib/contract/CalendarItemExperienceType';
import ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import getTxpObjectFromTxpEntities from 'owa-txp-common/lib/utils/getTxpObjectFromTxpEntities';
import CalendarEvent, {
    createEmptyCalendarEvent,
} from 'owa-calendar-types/lib/types/CalendarEvent';

// Create empty instance of CalendarEvent so that mobX doesn't freak out
const emptyCalendarEvent = createEmptyCalendarEvent();

/**
 * Converts a CalendarItem instance to CalendarEvent. This function will be used mostly from service
 * functions to convert the returned CalendarItem service objects to CalendarEvent instances for
 * consumption by the application.
 * @param calendarItem The calendar item (service object) to convert
 * @param mailboxInfo The mailbox info for the calendar event
 * @returns The converted CalendarEvent object
 */
export function calendarEvent(calendarItem: CalendarItem, mailboxInfo: MailboxInfo): CalendarEvent {
    // If no mailboxInfo is provided then get the user mailbox info
    mailboxInfo = mailboxInfo || getUserMailboxInfo();

    // Copy then overwrite with proper conversions
    return {
        ...emptyCalendarEvent,
        ...calendarItem,
        ...{
            ItemId: calendarItem.ItemId
                ? { ...calendarItem.ItemId, mailboxInfo: mailboxInfo }
                : calendarItem.ItemId,
            ParentFolderId: calendarItem.ParentFolderId
                ? { ...calendarItem.ParentFolderId, mailboxInfo: mailboxInfo }
                : calendarItem.ParentFolderId,
            Start: parseCalendarItemDate(calendarItem.Start),
            End: parseCalendarItemDate(calendarItem.End),
            StartTimeZoneId: cleanTimeZoneId(calendarItem.StartTimeZoneId),
            EndTimeZoneId: cleanTimeZoneId(calendarItem.EndTimeZoneId),
            LastModifiedTime: parseCalendarItemDate(calendarItem.LastModifiedTime),
            FreeBusyType: calendarItem.FreeBusyType as BusyType,
            ResponseType: calendarItem.ResponseType as ResponseTypeType,
            AppointmentReplyTime: parseCalendarItemDate(calendarItem.AppointmentReplyTime),
            ExperienceType: calendarItem.ExperienceType
                ? calendarItem.ExperienceType
                : ('Unspecified' as CalendarItemExperienceType),
            TxpObject: calendarItem.TailoredXpEntities
                ? getTxpObjectFromTxpEntities(
                      calendarItem.TailoredXpEntities,
                      calendarItem.EntityNamesMap
                  )
                : undefined,
            SeriesMasterItemId: calendarItem.SeriesMasterItemId
                ? { ...calendarItem.SeriesMasterItemId, mailboxInfo: mailboxInfo }
                : null,

            // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/43633
            // Treat any "private" group calendar events as if it was a normal event.
            // The only drawback I see with this is that IF one creates such a "private" event, say,
            // using Desktop Outlook, then it will show the lock icon and we won't. I don't think this
            // is a big deal, but if we decide that it is, then we will need to modify any other code that
            // depends on this property, beginning with owa-calendar-event-capabilities canEdit/canModify.
            Sensitivity:
                mailboxInfo.type == MailboxType.GroupMailbox &&
                calendarItem.Sensitivity == 'Private'
                    ? 'Normal'
                    : calendarItem.Sensitivity,
        },
    } as CalendarEvent;
}

function parseCalendarItemDate(date?: string) {
    // All production calls to calendarEvent() are done in response to
    // an EWS call or notification, which returns data in the user's time zone.
    // We can parse making this assumption, to handle dates outside the known DST
    // ranges without losing the offset returned by the server.
    if (date) {
        if (isFeatureEnabled('cal-assumeTzOffsetFromCalendarItem')) {
            const assumeDatesAreInThisTz = getConfig().timezone;
            const assumeOffsetFromStringIsCorrect = !!assumeDatesAreInThisTz;
            return parse(assumeDatesAreInThisTz, date, assumeOffsetFromStringIsCorrect);
        }
        return getDisplayDateFromEwsDate(date);
    }
    return undefined;
}

/**
 * Converts a CalendarEvent instance to CalendarItem. This function for the most part will be called
 * only within functions that deal directly with making service requests. Other code should only use
 * CalendarEvent instances.
 * @param calendarEvent The CalendarEvent object
 * @returns The converted CalendarItem instance
 */
export function toCalendarItem(calendarEvent: CalendarEvent): CalendarItem {
    return {
        __type: 'CalendarItem:#Exchange',
        ...calendarEvent,
        Start: calendarEvent.Start && getEwsRequestString(calendarEvent.Start),
        End: calendarEvent.End && getEwsRequestString(calendarEvent.End),
        LastModifiedTime:
            calendarEvent.LastModifiedTime && getEwsRequestString(calendarEvent.LastModifiedTime),
        AppointmentReplyTime:
            calendarEvent.AppointmentReplyTime &&
            getEwsRequestString(calendarEvent.AppointmentReplyTime),
        FreeBusyType: calendarEvent.FreeBusyType as string,
        CharmId: calendarEvent.CharmId,
        IsDraft: calendarEvent.IsDraft,
        IsRoomRequested: calendarEvent.IsRoomRequested,
    } as CalendarItem;
}
